import { type VirtualItem, type Virtualizer } from '@tanstack/react-virtual';
import { type MouseEvent, type MutableRefObject, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ArrowDownIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { cn } from '@air/utils-tailwind';

import { type WorkspaceAgentMessageFeedbackRating } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/feedback/WorkspaceAgentMessageFeedbackActions';
import { PrivateMessageUncitedAssetScope } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateMessageUncitedAssetScope';
import {
  PrivateWorkspaceAgentMessageListProvider,
  usePrivateWorkspaceAgentMessageListContext,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentMessageListContext';
import { PrivateWorkspaceAgentMessageListGuidedVariantFlow } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentMessageListGuidedVariantFlow';
import { PrivateWorkspaceAgentPanelEmptyThread } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelEmptyThread';
import { PrivateWorkspaceAgentPanelMessageBody } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelMessageBody';
import { PrivateWorkspaceAgentPanelMessageFeedback } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentPanelMessageFeedback';
import { PrivateWorkspaceAgentVariantNextActions } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentVariantNextActions';
import { WorkspaceAgentPanelElicitationAnswer } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/WorkspaceAgentPanelElicitationAnswer';
import { WorkspaceAgentPanelRunGroup } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/WorkspaceAgentPanelRunGroup';
import { PrivateWorkspaceAgentMonikerText } from '~/components/v2/components/workspace/workspace-agent-panel/composer-host/read/PrivateWorkspaceAgentMonikerText';
import { WorkspaceAgentBubble } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/bubble/WorkspaceAgentBubble';
import { usePrivateWorkspaceAgentOnboardingHandoffSession } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/onboarding/usePrivateWorkspaceAgentOnboardingHandoffSession';
import { useWorkspaceAgentHybridTurnParkLayout } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/onboarding/useWorkspaceAgentHybridTurnParkLayout';
import { usePrivateWorkspaceAgentMessagesAutoScroll } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentMessagesAutoScroll';
import { useWorkspaceAgentMessageHistoryPagination } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentMessageHistoryPagination';
import { useWorkspaceAgentMessageListVirtualizer } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentMessageListVirtualizer';
import { useWorkspaceAgentMessageLiveAnnouncement } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentMessageLiveAnnouncement';
import {
  usePrivateWorkspaceAgentPanelMessagePills,
  usePrivateWorkspaceAgentPanelMessages,
  usePrivateWorkspaceAgentPanelSession,
} from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { getWorkspaceAgentUserMessageBubbleVariant } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/displayMetadata';
import { type WorkspaceAgentMessage } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentMessage';
import {
  buildWorkspaceAgentMessageRowPills,
  createWorkspaceAgentMessageRowPillsCache,
  EMPTY_WORKSPACE_AGENT_MESSAGE_ROW_PILLS,
  type WorkspaceAgentMessageRowPills,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentMessageRowPills';
import { getPendingConsentDockElicitation } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getPendingConsentDockElicitation';
import { getPendingToolCallIdsFromElicitations } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getPendingToolCallIdsFromElicitations';
import { getWorkspaceAgentStreamingMessageId } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentStreamingMessageId';
import {
  groupWorkspaceAgentMessagesForDisplay,
  type WorkspaceAgentDisplayRow,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/groupWorkspaceAgentMessagesForDisplay';
import { shouldClearWorkspaceAgentElicitationFollowUp } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentElicitationFollowUpHold';
import { isWorkspaceAgentSessionRunning } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentSessionRunning';
import {
  getWorkspaceAgentDistanceFromBottomPx,
  getWorkspaceAgentScrollContainerMetrics,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/pinWorkspaceAgentScrollToBottom';
import { preserveWorkspaceAgentComposerFocus } from '~/components/v2/components/workspace/workspace-agent-panel/utils/preserveWorkspaceAgentComposerFocus';
import {
  scrollWorkspaceAgentDisplayRowStartIntoView,
  WORKSPACE_AGENT_DISPLAY_ROW_ID_ATTRIBUTE,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/scrollWorkspaceAgentDisplayRowStartIntoView';
import { shouldShowWorkspaceAgentVariantNextActions } from '~/components/v2/components/workspace/workspace-agent-panel/utils/shouldShowWorkspaceAgentVariantNextActions';
import {
  WORKSPACE_AGENT_JUMP_TO_BOTTOM_CATCH_UP_MIN_GAP_PX,
  WORKSPACE_AGENT_PANEL_CHAT_SCROLL_ATTRIBUTE,
} from '~/components/v2/components/workspace/workspace-agent-panel/workspaceAgentPanelConstants';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';

/** Shared so a pills-only row and a bubble row announce the speaker the same way. */
const USER_SPEAKER_LABEL = 'You said: ';

const hasCopyableAssistantText = (content: string | null | undefined) => (content ?? '').trim().length > 0;

type WorkspaceAgentMessageRatingsBySession = {
  ratings: Record<string, WorkspaceAgentMessageFeedbackRating>;
  sessionId: string | null;
};

type PrivateWorkspaceAgentMessageUncitedRowProps = {
  hasBubble: boolean;
  isPending: boolean;
  isUserMessage: boolean;
  uncitedPills: WorkspaceAgentMessageRowPills['uncitedPills'];
};

const PrivateWorkspaceAgentMessageUncitedRow = memo(
  ({ hasBubble, isPending, isUserMessage, uncitedPills }: PrivateWorkspaceAgentMessageUncitedRowProps) => (
    <div
      aria-busy={isPending || undefined}
      className={cn('flex w-full flex-col items-end gap-2', isPending && 'opacity-80')}
      data-testid="WORKSPACE_AGENT_MESSAGE_UNCITED_ROW"
    >
      {isUserMessage && !hasBubble ? <span className="sr-only">{USER_SPEAKER_LABEL}</span> : null}
      <PrivateMessageUncitedAssetScope pills={uncitedPills} />
    </div>
  ),
);

PrivateWorkspaceAgentMessageUncitedRow.displayName = 'PrivateWorkspaceAgentMessageUncitedRow';

type PrivateWorkspaceAgentMessageBubbleRowProps = {
  isPending: boolean;
  isUserMessage: boolean;
  message: WorkspaceAgentMessage;
  pills: WorkspaceAgentMessageRowPills['pills'];
};

const PrivateWorkspaceAgentMessageBubbleRow = memo(
  ({ isPending, isUserMessage, message, pills }: PrivateWorkspaceAgentMessageBubbleRowProps) => {
    const { latestAssistantMessageId, messageRatings, streamingMessageId } =
      usePrivateWorkspaceAgentMessageListContext();
    const isStreamingAssistant = !isUserMessage && message.id === streamingMessageId;
    const showFeedback =
      !isUserMessage &&
      !isStreamingAssistant &&
      hasCopyableAssistantText(message.content) &&
      message.id === latestAssistantMessageId;

    const bubble = (
      <WorkspaceAgentBubble
        data-testid="WORKSPACE_AGENT_MESSAGE"
        isPending={isPending}
        variant={isUserMessage ? getWorkspaceAgentUserMessageBubbleVariant(message.metadata) : 'agent'}
      >
        <span className="sr-only">{isUserMessage ? USER_SPEAKER_LABEL : 'Agent said: '}</span>
        <PrivateWorkspaceAgentPanelMessageBody
          isStreaming={message.id === streamingMessageId}
          message={message}
          pills={pills}
        />
      </WorkspaceAgentBubble>
    );

    if (!showFeedback) {
      return bubble;
    }

    return (
      <div className="flex w-full flex-col items-start">
        {bubble}
        <PrivateWorkspaceAgentPanelMessageFeedback
          message={message}
          pills={pills}
          rating={messageRatings[message.id]}
        />
      </div>
    );
  },
);

PrivateWorkspaceAgentMessageBubbleRow.displayName = 'PrivateWorkspaceAgentMessageBubbleRow';

type PrivateWorkspaceAgentMessageRowProps = {
  isAwaitingElicitationFollowUp: boolean;
  pillsByRowId: ReadonlyMap<string, WorkspaceAgentMessageRowPills>;
  row: WorkspaceAgentDisplayRow;
};

const PrivateWorkspaceAgentMessageRow = memo(
  ({ isAwaitingElicitationFollowUp, pillsByRowId, row }: PrivateWorkspaceAgentMessageRowProps) => {
    if (row.type === 'runGroup') {
      return (
        <WorkspaceAgentPanelRunGroup
          finishedAt={row.finishedAt}
          isAwaitingElicitationFollowUp={isAwaitingElicitationFollowUp}
          outcome={row.outcome}
          startedAt={row.startedAt}
          status={row.status}
          tools={row.tools}
        />
      );
    }

    if (row.type === 'elicitationAnswer') {
      return (
        <WorkspaceAgentPanelElicitationAnswer
          answer={row.answer}
          question={row.question === null ? null : <PrivateWorkspaceAgentMonikerText value={row.question} />}
        />
      );
    }

    const isUserMessage = row.message.role === 'user';
    const { displayHasBubble, hasBubble, pills, showUncitedAssetPreview, uncitedPills } =
      pillsByRowId.get(row.id) ?? EMPTY_WORKSPACE_AGENT_MESSAGE_ROW_PILLS;

    const showDedupedPillsOnlySpeakerLabel = isUserMessage && !showUncitedAssetPreview && !displayHasBubble;

    return (
      <>
        {showUncitedAssetPreview ? (
          <PrivateWorkspaceAgentMessageUncitedRow
            hasBubble={hasBubble}
            isPending={row.message.isPending}
            isUserMessage={isUserMessage}
            uncitedPills={uncitedPills}
          />
        ) : null}
        {showDedupedPillsOnlySpeakerLabel ? <span className="sr-only">{USER_SPEAKER_LABEL}</span> : null}
        {displayHasBubble ? (
          <PrivateWorkspaceAgentMessageBubbleRow
            isPending={row.message.isPending}
            isUserMessage={isUserMessage}
            message={row.message}
            pills={pills}
          />
        ) : null}
      </>
    );
  },
);

PrivateWorkspaceAgentMessageRow.displayName = 'PrivateWorkspaceAgentMessageRow';

type PrivateWorkspaceAgentVirtualizedMessageRowsProps = {
  isAwaitingElicitationFollowUp: boolean;
  pillsByRowId: ReadonlyMap<string, WorkspaceAgentMessageRowPills>;
  rows: WorkspaceAgentDisplayRow[];
  /** The list already starts after the onboarding prefix, so this offset is not part of the row translation. */
  scrollMargin: number;
  totalSize: number;
  virtualItems: VirtualItem<Element>[];
  virtualizer: Virtualizer<HTMLElement, Element>;
};

const PrivateWorkspaceAgentVirtualizedMessageRows = memo(
  ({
    isAwaitingElicitationFollowUp,
    pillsByRowId,
    rows,
    scrollMargin,
    totalSize,
    virtualItems,
    virtualizer,
  }: PrivateWorkspaceAgentVirtualizedMessageRowsProps) => {
    const latestRunGroupId = rows.findLast((row) => row.type === 'runGroup')?.id;

    return (
      <div className="relative w-full" style={{ height: totalSize }}>
        <div
          className="absolute top-0 left-0 flex w-full flex-col px-4"
          style={{ transform: `translateY(${(virtualItems[0]?.start ?? 0) - scrollMargin}px)` }}
        >
          {virtualItems.map((virtualRow) => {
            const row = rows[virtualRow.index];

            if (!row) {
              return null;
            }

            return (
              <div
                className="flex flex-col gap-3 pb-3"
                data-index={virtualRow.index}
                key={virtualRow.key}
                ref={virtualizer.measureElement}
                {...{ [WORKSPACE_AGENT_DISPLAY_ROW_ID_ATTRIBUTE]: row.id }}
              >
                <PrivateWorkspaceAgentMessageRow
                  isAwaitingElicitationFollowUp={isAwaitingElicitationFollowUp && row.id === latestRunGroupId}
                  pillsByRowId={pillsByRowId}
                  row={row}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);

PrivateWorkspaceAgentVirtualizedMessageRows.displayName = 'PrivateWorkspaceAgentVirtualizedMessageRows';

type PrivateWorkspaceAgentMessageListLogProps = {
  hybridTurnMinHeight: number;
  isAwaitingElicitationFollowUp: boolean;
  isHybridTurnParked: boolean;
  isLoadingEarlierMessages: boolean;
  isLoadingEmptyThread: boolean;
  loadOlderControlBlockRef: MutableRefObject<HTMLDivElement | null>;
  onboardingPrefixRef: MutableRefObject<HTMLDivElement | null>;
  onLoadOlderMessagesClick: (event: MouseEvent<HTMLButtonElement>) => void;
  pillsByRowId: ReadonlyMap<string, WorkspaceAgentMessageRowPills>;
  rows: WorkspaceAgentDisplayRow[];
  scrollMargin: number;
  showLoadOlderMessages: boolean;
  showOnboardingSimulation: boolean;
  showVariantNextActions: boolean;
  showZeroState: boolean;
  totalSize: number;
  virtualItems: VirtualItem<Element>[];
  virtualizer: Virtualizer<HTMLElement, Element>;
};

const PrivateWorkspaceAgentMessageListLog = memo(
  ({
    hybridTurnMinHeight,
    isAwaitingElicitationFollowUp,
    isHybridTurnParked,
    isLoadingEarlierMessages,
    isLoadingEmptyThread,
    loadOlderControlBlockRef,
    onboardingPrefixRef,
    onLoadOlderMessagesClick,
    pillsByRowId,
    rows,
    scrollMargin,
    showLoadOlderMessages,
    showOnboardingSimulation,
    showVariantNextActions,
    showZeroState,
    totalSize,
    virtualItems,
    virtualizer,
  }: PrivateWorkspaceAgentMessageListLogProps) => {
    const isLogHorizontallyInset = showZeroState || (showOnboardingSimulation && rows.length === 0);

    return (
      <div
        aria-busy={isLoadingEmptyThread || undefined}
        aria-label="Message history"
        aria-live="off"
        className={cn('flex min-h-full w-full flex-1 flex-col', isLogHorizontallyInset && 'px-4')}
        data-testid="WORKSPACE_AGENT_MESSAGE_LIST"
        role="log"
        style={hybridTurnMinHeight > 0 ? { minHeight: hybridTurnMinHeight } : undefined}
      >
        {isLoadingEmptyThread ? (
          <span className="sr-only" data-testid="WORKSPACE_AGENT_MESSAGE_LIST_LOADING_STATUS" role="status">
            Loading conversation
          </span>
        ) : null}
        <output className="sr-only" data-testid="WORKSPACE_AGENT_MESSAGE_LIST_LOADING_EARLIER_STATUS">
          {isLoadingEarlierMessages ? 'Loading earlier messages' : ''}
        </output>
        {showZeroState && !showOnboardingSimulation ? <PrivateWorkspaceAgentPanelEmptyThread /> : null}
        {showOnboardingSimulation ? (
          <div className={cn(rows.length > 0 && 'px-4')} ref={onboardingPrefixRef}>
            <PrivateWorkspaceAgentPanelEmptyThread />
          </div>
        ) : null}
        {rows.length > 0 ? (
          <div className={cn('flex w-full flex-col', !isHybridTurnParked && 'mt-auto')}>
            {showLoadOlderMessages ? (
              <div
                className="flex shrink-0 justify-center px-4 pt-2 pb-2"
                data-testid="WORKSPACE_AGENT_LOAD_OLDER_MESSAGES_CONTROL"
                ref={loadOlderControlBlockRef}
              >
                <Button
                  appearance="outline"
                  color="gray"
                  data-testid="WORKSPACE_AGENT_LOAD_OLDER_MESSAGES_BUTTON"
                  disabled={isLoadingEarlierMessages}
                  onClick={onLoadOlderMessagesClick}
                  onMouseDown={preserveWorkspaceAgentComposerFocus}
                  size="small"
                  type="button"
                >
                  Load older messages
                </Button>
              </div>
            ) : null}
            <PrivateWorkspaceAgentVirtualizedMessageRows
              isAwaitingElicitationFollowUp={isAwaitingElicitationFollowUp}
              pillsByRowId={pillsByRowId}
              rows={rows}
              scrollMargin={scrollMargin}
              totalSize={totalSize}
              virtualItems={virtualItems}
              virtualizer={virtualizer}
            />
            <PrivateWorkspaceAgentVariantNextActions show={showVariantNextActions} />
          </div>
        ) : null}
        <PrivateWorkspaceAgentMessageListGuidedVariantFlow
          className={cn(isLogHorizontallyInset && 'mt-auto', !isLogHorizontallyInset && 'px-4')}
        />
      </div>
    );
  },
);

PrivateWorkspaceAgentMessageListLog.displayName = 'PrivateWorkspaceAgentMessageListLog';

/** `role="log"` stays mounted when empty so the first reply has a region to announce into. */
export const PrivateWorkspaceAgentPanelMessageList = memo(() => {
  const session = usePrivateWorkspaceAgentPanelSession();
  const messages = usePrivateWorkspaceAgentPanelMessages();
  const pillsByMessageId = usePrivateWorkspaceAgentPanelMessagePills();
  // Reused across rebuilds so an untouched row keeps the array identities its children memo on.
  // Lazily initialized: passing a built map as an argument would make a throwaway one per render.
  const [rowPillsCache] = useState(createWorkspaceAgentMessageRowPillsCache);

  const pendingConsentDockElicitation = useMemo(
    () => getPendingConsentDockElicitation(session.pendingElicitations),
    [session.pendingElicitations],
  );

  const pendingToolCallIds = useMemo(
    () => getPendingToolCallIdsFromElicitations(session.pendingElicitations),
    [session.pendingElicitations],
  );

  const rows = useMemo(
    () =>
      groupWorkspaceAgentMessagesForDisplay({
        activeRunId: session.activeRunId,
        isAwaitingRunStart: session.isAwaitingRunStart,
        messages,
        pendingToolCallIds,
        runsById: session.runsById,
      }),
    [session.activeRunId, session.isAwaitingRunStart, messages, pendingToolCallIds, session.runsById],
  );

  const showVariantNextActions = useMemo(
    () => shouldShowWorkspaceAgentVariantNextActions({ messages, session }),
    [messages, session],
  );

  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // One pass per change, not per row per render: streaming would otherwise walk the history on every
  // token and hand the body a fresh array identity, defeating its `memo`.
  const pillsByRowId = useMemo(
    () => buildWorkspaceAgentMessageRowPills({ cache: rowPillsCache, pillsByMessageId, rows }),
    [pillsByMessageId, rowPillsCache, rows],
  );

  const lastMessage = messages.at(-1);
  // Stick-to-bottom would pull the scripted example back down over "Now it's your turn".
  const isPinSuspended = useWorkspaceAgentPanelStore((state) => state.isOnboardingHybridTurnLineVisible);
  const { showOnboardingSimulation } = usePrivateWorkspaceAgentOnboardingHandoffSession(session.sessionId);

  const runJumpCatchUpOnEndRef = useRef<(() => void) | undefined>(undefined);
  const runVirtualizerJumpToBottomRef = useRef<(() => void) | undefined>(undefined);
  const loadOlderControlBlockRef = useRef<HTMLDivElement | null>(null);
  const focusComposer = useWorkspaceAgentPanelStore((state) => state.focusComposer);

  const {
    catchUpPinAfterSmoothRef,
    isPinnedToBottomRef,
    isProgrammaticPinRef,
    isSmoothProgrammaticPinRef,
    jumpToBottom,
    programmaticPinFrameRef,
    scrollContainerRef,
    showJumpToBottom,
    suppressMeasureTailPinRef,
  } = usePrivateWorkspaceAgentMessagesAutoScroll({
    activeRunId: session.activeRunId,
    isAwaitingRunStart: session.isAwaitingRunStart,
    isLastMessageOwn: lastMessage?.role === 'user' && lastMessage.isPending,
    isPinSuspended,
    lastElicitationId: pendingConsentDockElicitation?.id ?? null,
    lastMessageId: lastMessage?.id ?? null,
    runJumpCatchUpOnEndRef,
    runVirtualizerJumpToBottomRef,
    sessionId: session.sessionId,
  });

  const { chatScrollContainerStyle, hybridTurnLayout, isHybridTurnParked, onboardingPrefixRef } =
    useWorkspaceAgentHybridTurnParkLayout({
      displayRowCount: rows.length,
      scrollContainerRef,
      suppressMeasureTailPinRef,
    });

  const { totalSize, virtualItems, virtualizer } = useWorkspaceAgentMessageListVirtualizer({
    catchUpPinAfterSmoothRef,
    suppressMeasureTailPinRef,
    isPinnedToBottomRef,
    isSmoothProgrammaticPinRef,
    pillsByRowId,
    rows,
    scrollContainerRef,
    scrollMargin: hybridTurnLayout.scrollMargin,
    sessionId: session.sessionId,
  });

  const { isLoadingEarlierMessages, loadOlderMessages, showLoadOlderMessages } =
    useWorkspaceAgentMessageHistoryPagination({
      displayRowCount: rows.length,
      fetchNextMessagesPage: session.fetchNextMessagesPage,
      focusComposer,
      hasNextMessagesPage: session.hasNextMessagesPage,
      isFetchingNextMessagesPage: session.isFetchingNextMessagesPage,
      isPinnedToBottomRef,
      isProgrammaticPinRef,
      loadOlderControlBlockRef,
      programmaticPinFrameRef,
      scrollContainerRef,
      sessionId: session.sessionId,
      suppressMeasureTailPinRef,
      virtualizedTotalSize: totalSize,
    });

  runVirtualizerJumpToBottomRef.current = () => {
    const container = scrollContainerRef.current;

    if (container === null || rows.length === 0) {
      return;
    }

    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  };

  runJumpCatchUpOnEndRef.current = () => {
    const container = scrollContainerRef.current;

    if (container === null || rows.length === 0) {
      return;
    }

    const metrics = getWorkspaceAgentScrollContainerMetrics(container);

    if (getWorkspaceAgentDistanceFromBottomPx(metrics) <= WORKSPACE_AGENT_JUMP_TO_BOTTOM_CATCH_UP_MIN_GAP_PX) {
      return;
    }

    container.scrollTo({ top: container.scrollHeight });
  };

  const handleLoadOlderMessagesClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      loadOlderMessages({
        focusComposerAfter: event.detail === 0,
      });
    },
    [loadOlderMessages],
  );

  const scrollDisplayRowStartIntoView = useCallback(
    (displayRowId: string) => {
      const displayRowIndex = rowsRef.current.findIndex((row) => row.id === displayRowId);

      scrollWorkspaceAgentDisplayRowStartIntoView({
        displayRowId,
        displayRowIndex,
        isPinnedToBottomRef,
        scrollContainerRef,
        suppressMeasureTailPinRef,
        virtualizer,
      });
    },
    [isPinnedToBottomRef, scrollContainerRef, suppressMeasureTailPinRef, virtualizer],
  );

  const handleJumpToBottomClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (event.detail === 0) {
        scrollContainerRef.current?.focus({ preventScroll: true });
      }

      jumpToBottom();
    },
    [jumpToBottom, scrollContainerRef],
  );

  // Read the run state, not the header status: that reads `waiting` during a consent prompt while
  // streaming continues.
  const streamingMessageId = useMemo(
    () =>
      getWorkspaceAgentStreamingMessageId({
        activeRunId: session.activeRunId,
        isAwaitingRunStart: session.isAwaitingRunStart,
        isSending: session.isSending,
        isSessionProcessing: session.isSessionProcessing,
        rows,
      }),
    [rows, session.activeRunId, session.isAwaitingRunStart, session.isSending, session.isSessionProcessing],
  );

  const followUpAssistantMessageIds = session.elicitationFollowUpAssistantMessageIds;

  useEffect(() => {
    if (!followUpAssistantMessageIds) {
      return;
    }

    const sessionStillRunning = rows.some(
      (row) => row.type === 'runGroup' && (row.status === 'active' || row.status === 'cancelling'),
    );

    const sessionIsActive =
      sessionStillRunning || isWorkspaceAgentSessionRunning(session) || session.isSessionAwaitingInput;

    if (sessionIsActive && !session.elicitationFollowUpSawSessionActivity) {
      session.noteElicitationFollowUpSawSessionActivity();
    }

    if (
      shouldClearWorkspaceAgentElicitationFollowUp({
        assistantMessageIds: followUpAssistantMessageIds,
        isAwaitingRunStart: session.isAwaitingRunStart,
        isSending: session.isSending,
        isSessionAwaitingInput: session.isSessionAwaitingInput,
        isSessionProcessing: session.isSessionProcessing,
        messages,
        rows,
        sawSessionActivity: session.elicitationFollowUpSawSessionActivity,
        streamingMessageId,
      })
    ) {
      session.clearElicitationFollowUp();
    }
  }, [followUpAssistantMessageIds, messages, rows, session, streamingMessageId]);

  const latestAssistantMessageId = useMemo(() => {
    for (let index = rows.length - 1; index >= 0; index -= 1) {
      const row = rows[index];

      if (
        row?.type === 'message' &&
        row.message.role === 'assistant' &&
        row.message.id !== streamingMessageId &&
        hasCopyableAssistantText(row.message.content)
      ) {
        return row.message.id;
      }
    }

    return null;
  }, [rows, streamingMessageId]);

  const [messageRatingsBySession, setMessageRatingsBySession] = useState<WorkspaceAgentMessageRatingsBySession>({
    ratings: {},
    sessionId: null,
  });

  const messageRatings = useMemo(
    () => (messageRatingsBySession.sessionId === session.sessionId ? messageRatingsBySession.ratings : {}),
    [messageRatingsBySession, session.sessionId],
  );

  const handleRateMessage = useCallback(
    (messageId: string, rating: WorkspaceAgentMessageFeedbackRating) => {
      setMessageRatingsBySession((current) => {
        const activeSessionId = session.sessionId;

        if (current.sessionId !== activeSessionId) {
          return {
            sessionId: activeSessionId,
            ratings: { [messageId]: rating },
          };
        }

        return {
          sessionId: activeSessionId,
          ratings: { ...current.ratings, [messageId]: rating },
        };
      });
    },
    [session.sessionId],
  );

  const isEmpty = rows.length === 0;

  // A conversation opened from the session list is empty only until its history arrives, and
  // inviting the user to start one they already started would be replaced a moment later.
  const showZeroState = isEmpty && !session.isLoadingMessages && !showOnboardingSimulation;
  const isLoadingEmptyThread = isEmpty && session.isLoadingMessages;

  const liveAnnouncement = useWorkspaceAgentMessageLiveAnnouncement({
    isLoadingMessages: session.isLoadingMessages,
    messages,
    sessionId: session.sessionId,
  });

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
      <section
        aria-label="Agent conversation"
        className="flex min-h-0 flex-1 flex-col overflow-y-auto"
        data-testid="WORKSPACE_AGENT_PANEL_CHAT_VIEW"
        {...{ [WORKSPACE_AGENT_PANEL_CHAT_SCROLL_ATTRIBUTE]: '' }}
        ref={scrollContainerRef}
        style={chatScrollContainerStyle}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region has to be focusable to be scrollable from the keyboard, which is what axe's scrollable-region-focusable asks for
        tabIndex={0}
      >
        <span aria-live="polite" className="sr-only">
          {liveAnnouncement}
        </span>
        <PrivateWorkspaceAgentMessageListProvider
          latestAssistantMessageId={latestAssistantMessageId}
          messageRatings={messageRatings}
          onRateMessage={handleRateMessage}
          scrollDisplayRowStartIntoView={scrollDisplayRowStartIntoView}
          streamingMessageId={streamingMessageId}
        >
          <PrivateWorkspaceAgentMessageListLog
            hybridTurnMinHeight={hybridTurnLayout.minHeight}
            isAwaitingElicitationFollowUp={session.isAwaitingElicitationFollowUp}
            isHybridTurnParked={isHybridTurnParked}
            isLoadingEarlierMessages={isLoadingEarlierMessages}
            isLoadingEmptyThread={isLoadingEmptyThread}
            loadOlderControlBlockRef={loadOlderControlBlockRef}
            onboardingPrefixRef={onboardingPrefixRef}
            onLoadOlderMessagesClick={handleLoadOlderMessagesClick}
            pillsByRowId={pillsByRowId}
            rows={rows}
            scrollMargin={hybridTurnLayout.scrollMargin}
            showLoadOlderMessages={showLoadOlderMessages}
            showOnboardingSimulation={showOnboardingSimulation}
            showVariantNextActions={showVariantNextActions}
            showZeroState={showZeroState}
            totalSize={totalSize}
            virtualItems={virtualItems}
            virtualizer={virtualizer}
          />
        </PrivateWorkspaceAgentMessageListProvider>
      </section>
      {showJumpToBottom ? (
        <button
          aria-label="Jump to bottom"
          className="absolute bottom-7 left-1/2 z-20 flex size-8 -translate-x-1/2 items-center justify-center rounded-full border border-gray-4 bg-gray-1 text-gray-12 shadow-m transition-colors hover:border-gray-5 hover:bg-gray-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-altitude-9 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-1 forced-colors:focus-visible:outline forced-colors:focus-visible:outline-2"
          data-testid="WORKSPACE_AGENT_JUMP_TO_BOTTOM_BUTTON"
          onClick={handleJumpToBottomClick}
          onMouseDown={preserveWorkspaceAgentComposerFocus}
          type="button"
        >
          <ArrowDownIcon aria-hidden className="text-gray-9" size={16} />
        </button>
      ) : null}
    </div>
  );
});

PrivateWorkspaceAgentPanelMessageList.displayName = 'PrivateWorkspaceAgentPanelMessageList';
